// React framework integration with hooks

import { useState, useEffect, useContext, createContext, ReactNode } from 'react'
import type { Session } from '../types'

// React context for authentication
interface AuthContextType {
  session: Session | null
  loading: boolean
  error: string | null
  signIn: (provider: string, credentials?: any) => Promise<void>
  signOut: () => Promise<void>
  refreshSession: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

// Auth provider component
export interface AuthProviderProps {
  children: ReactNode
  auth: any
  config?: any
}

export function AuthProvider({ children, auth, config }: AuthProviderProps) {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Initialize auth and load session
    const initAuth = async () => {
      try {
        await auth.initialize()
        
        // Try to get existing session from cookie
        const sessionCookie = document.cookie
          .split('; ')
          .find(row => row.startsWith('kitium.session='))
          ?.split('=')[1]

        if (sessionCookie) {
          try {
            const sessionData = JSON.parse(decodeURIComponent(sessionCookie))
            const validSession = await auth.getSession(sessionData.id)
            if (validSession) {
              setSession(validSession)
            }
          } catch (err) {
            console.warn('Invalid session cookie:', err)
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Authentication initialization failed')
      } finally {
        setLoading(false)
      }
    }

    initAuth()
  }, [auth])

  const signIn = async (provider: string, credentials?: any) => {
    setLoading(true)
    setError(null)

    try {
      // In a real implementation, you'd handle OAuth flow or credential validation
      const newSession = await auth.createSession('user_123') // Mock user ID
      setSession(newSession)
      
      // Set session cookie
      document.cookie = `kitium.session=${encodeURIComponent(JSON.stringify(newSession))}; path=/; secure; samesite=lax`
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign in failed')
      throw err
    } finally {
      setLoading(false)
    }
  }

  const signOut = async () => {
    setLoading(true)
    setError(null)

    try {
      if (session) {
        await auth.deleteSession(session.id)
      }
      
      setSession(null)
      
      // Clear session cookie
      document.cookie = 'kitium.session=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT'
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign out failed')
    } finally {
      setLoading(false)
    }
  }

  const refreshSession = async () => {
    if (!session) return

    try {
      const refreshedSession = await auth.refreshSession(session.id)
      if (refreshedSession) {
        setSession(refreshedSession)
        
        // Update session cookie
        document.cookie = `kitium.session=${encodeURIComponent(JSON.stringify(refreshedSession))}; path=/; secure; samesite=lax`
      } else {
        // Session expired, sign out
        await signOut()
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Session refresh failed')
    }
  }

  const value: AuthContextType = {
    session,
    loading,
    error,
    signIn,
    signOut,
    refreshSession
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

// Hook to use authentication context
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Hook to check if user has specific entitlements
export function useEntitlements(requiredEntitlements: string[]): boolean {
  const { session } = useAuth()
  
  if (!session) return false
  
  return requiredEntitlements.every(entitlement => 
    session.entitlements.includes(entitlement)
  )
}

// Hook to check if user has specific entitlement
export function useEntitlement(entitlement: string): boolean {
  const { session } = useAuth()
  
  if (!session) return false
  
  return session.entitlements.includes(entitlement)
}

// Hook to get user's plan
export function usePlan(): string | null {
  const { session } = useAuth()
  return session?.plan || null
}

// Hook to get user's organization
export function useOrganization(): string | null {
  const { session } = useAuth()
  return session?.orgId || null
}

// Higher-order component for protecting routes
export function withAuth<T extends object>(
  Component: React.ComponentType<T>,
  options?: {
    requiredEntitlements?: string[]
    redirectTo?: string
    fallback?: React.ComponentType
  }
) {
  return function AuthenticatedComponent(props: T) {
    const { session, loading } = useAuth()
    const { requiredEntitlements, redirectTo, fallback: Fallback } = options || {}

    if (loading) {
      return <div>Loading...</div>
    }

    if (!session) {
      if (redirectTo) {
        window.location.href = redirectTo
        return null
      }
      return Fallback ? <Fallback /> : <div>Please sign in to access this page.</div>
    }

    if (requiredEntitlements) {
      const hasEntitlements = requiredEntitlements.every(entitlement =>
        session.entitlements.includes(entitlement)
      )

      if (!hasEntitlements) {
        return <div>You don't have permission to access this page.</div>
      }
    }

    return <Component {...props} />
  }
}

// Component for conditional rendering based on entitlements
export interface EntitlementGateProps {
  entitlements: string[]
  children: ReactNode
  fallback?: ReactNode
}

export function EntitlementGate({ entitlements, children, fallback }: EntitlementGateProps) {
  const hasEntitlements = useEntitlements(entitlements)
  
  if (hasEntitlements) {
    return <>{children}</>
  }
  
  return <>{fallback || null}</>
}

// Component for conditional rendering based on plan
export interface PlanGateProps {
  plans: string[]
  children: ReactNode
  fallback?: ReactNode
}

export function PlanGate({ plans, children, fallback }: PlanGateProps) {
  const currentPlan = usePlan()
  
  if (currentPlan && plans.includes(currentPlan)) {
    return <>{children}</>
  }
  
  return <>{fallback || null}</>
}
